import { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Copy, FileUp, Loader2 } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { billEmailService, type InboxUploadResult } from '../../services/billEmailService';
import { isZip, pickDroppedFiles, unzipPdfs, validateInvoicePdf } from '../../utils/invoiceDrop';
import { useBlockStrayFileDrop } from '../../utils/useBlockStrayFileDrop';

// Drop PDFs into the inbox (Erin, 29 Oct 2026): bills that only exist on a
// retailer's portal, downloaded and dragged in. Each file becomes its own inbox
// item and goes through the same ingest as an emailed invoice — read, matched
// to a site, assigned and parsed when everything is known, otherwise left here
// for a person. Nothing dropped is ever silently lost: every file gets a line.
// A zip is opened here and each PDF in it handled as if dropped on its own.

type ItemState = 'uploading' | 'assigned' | 'matched' | 'unmatched' | 'duplicate' | 'failed';

type DropItem = { key: string; name: string; state: ItemState; message: string };

/** Two at a time: each one is an extraction and an LLM call on the server. */
const CONCURRENCY = 2;

const outcome = (r: InboxUploadResult): Pick<DropItem, 'state' | 'message'> => {
  if (r.status === 'duplicate') return { state: 'duplicate', message: 'Already in the inbox' };
  if (r.status === 'filtered') return { state: 'failed', message: r.reason ?? 'Not added' };
  // A PDF holding several invoices was cut into one item each; the server says how it went.
  if (r.reason) return { state: 'matched', message: r.reason };
  if (r.assigned) return { state: 'assigned', message: 'Assigned to its site — parsing' };
  if (r.inboxStatus === 'matched_auto') return { state: 'matched', message: 'Matched a site — check and assign' };
  return { state: 'unmatched', message: 'In the inbox — no site matched yet' };
};

const ICON: Record<ItemState, React.ElementType> = {
  uploading: Loader2,
  assigned: CheckCircle2,
  matched: CheckCircle2,
  unmatched: AlertTriangle,
  duplicate: Copy,
  failed: AlertTriangle,
};

const TONE: Record<ItemState, string> = {
  uploading: 'text-gray-500',
  assigned: 'text-emerald-700',
  matched: 'text-blue-700',
  unmatched: 'text-amber-700',
  duplicate: 'text-gray-500',
  failed: 'text-red-600',
};

type InboxDropZoneProps = {
  /** Called once a batch finishes, so the list and counts can reload. */
  onUploaded: () => void;
};

export const InboxDropZone = ({ onUploaded }: InboxDropZoneProps) => {
  const [items, setItems] = useState<DropItem[]>([]);
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = items.some((i) => i.state === 'uploading');

  // A drop that misses the zone must not navigate the tab to the PDF.
  useBlockStrayFileDrop(true);

  const update = (key: string, patch: Pick<DropItem, 'state' | 'message'>) =>
    setItems((cur) => cur.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const addFiles = async (dropped: File[]) => {
    if (!dropped.length) return;

    // A retailer's bulk download is often a zip: every PDF in it is its own
    // invoice. Anything else in the zip gets a line saying it was left out.
    const files: File[] = [];
    const notes: DropItem[] = [];
    for (const file of dropped) {
      if (!isZip(file)) { files.push(file); continue; }
      try {
        const { pdfs, skipped } = await unzipPdfs(file);
        files.push(...pdfs);
        if (!pdfs.length) notes.push({ key: crypto.randomUUID(), name: file.name, state: 'failed', message: 'No PDFs in this zip' });
        for (const name of skipped) {
          notes.push({ key: crypto.randomUUID(), name, state: 'failed', message: `Skipped from ${file.name} — not a PDF, or past the 50-file limit` });
        }
      } catch {
        notes.push({ key: crypto.randomUUID(), name: file.name, state: 'failed', message: 'This zip could not be opened' });
      }
    }
    if (notes.length) setItems((cur) => [...notes, ...cur]);
    if (!files.length) return;

    const batch = files.map((file) => ({ file, key: crypto.randomUUID() }));
    setItems((cur) => [
      ...batch.map(({ file, key }) => {
        const invalid = validateInvoicePdf(file);
        return { key, name: file.name, state: (invalid ? 'failed' : 'uploading') as ItemState, message: invalid ?? 'Reading the invoice…' };
      }),
      ...cur,
    ]);

    const queue = batch.filter(({ file }) => !validateInvoicePdf(file));
    const worker = async () => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        const res = await billEmailService.uploadInvoice(next.file);
        update(next.key, res.success && res.data ? outcome(res.data) : { state: 'failed', message: res.error ?? 'Upload failed' });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
    onUploaded();
  };

  return (
    <div className="mb-4">
      <div
        data-testid="inbox-drop-zone"
        data-over={over}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void addFiles(pickDroppedFiles(e.dataTransfer));
        }}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-4 text-sm cursor-pointer transition-colors',
          over ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
        )}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
        <span>
          Drop invoice PDFs or a zip of them here, or click to choose — each PDF is read and matched to its site like an emailed invoice
        </span>
        <input
          ref={inputRef}
          data-testid="inbox-drop-input"
          type="file"
          accept="application/pdf,.pdf,application/zip,.zip"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            void addFiles(files);
          }}
        />
      </div>

      {items.length > 0 && (
        <div data-testid="inbox-drop-results" className="mt-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 space-y-1">
          <div className="flex justify-between text-xs text-gray-500">
            <span>{items.filter((i) => i.state === 'uploading').length > 0 ? 'Adding to the inbox…' : 'Added to the inbox'}</span>
            {!busy && (
              <button type="button" className="hover:text-gray-800" onClick={() => setItems([])}>Clear</button>
            )}
          </div>
          {items.map((i) => {
            const Icon = ICON[i.state];
            return (
              <div key={i.key} data-testid="inbox-drop-item" data-state={i.state} className="flex items-center gap-2 text-xs">
                <Icon className={cn('h-3.5 w-3.5 shrink-0', TONE[i.state], i.state === 'uploading' && 'animate-spin')} />
                <span className="truncate text-gray-800 max-w-[45%]" title={i.name}>{i.name}</span>
                <span className={cn('truncate', TONE[i.state])}>{i.message}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
